/** @jsx isolateComponent */

import React from 'react';
import { Link } from 'components/Link/Link';
import { labelStyles, isolateComponent } from '@zapier/style-encapsulation';
import { css } from '@emotion/core';
import { Animation, Colors, Typography, Icon } from '@zapier/design-system';
import styled from '@emotion/styled';

type CollapsibleMenuProps = {
  title: string;
  onChildClick: (e: React.MouseEvent) => void;
  isOpen: boolean;
  onClick: () => void;
  submenu: Array<{
    href: string;
    text: string;
  }>;
};

export const CollapsibleMenu: React.FC<CollapsibleMenuProps> = ({
  title,
  submenu,
  onChildClick,
  isOpen,
  ...otherProps
}) => {
  return (
    <details open={isOpen} {...otherProps}>
      <summary css={Styles.summary()}>
        <div css={Styles.summaryInner}>
          <span>{title}</span>
          <span css={Styles.caret(isOpen)}>
            <Icon isBlock={true} name="arrowSmallDown" size={24} />
          </span>
        </div>
      </summary>
      <ul css={Styles.list}>
        {submenu.map((submenuItem) => (
          <li key={submenuItem.href}>
            <StyledLink onClick={onChildClick} href={submenuItem.href}>
              <div css={Styles.linkInner}>
                <p css={Styles.linkTitle}>{submenuItem.text}</p>
              </div>
            </StyledLink>
          </li>
        ))}
      </ul>
    </details>
  );
};

const Styles = labelStyles('UniversalSidebarSubmenu', {
  summary: () => [
    css`
      display: block;
      border-radius: 10px;
      color: ${Colors.grey700};

      &:hover,
      &:focus {
        color: ${Colors.black};
        background-color: ${Colors.grey100};
      }

      [open] > & {
        color: ${Colors.blue500};
        background-color: ${Colors.blue100};
      }
    `,
    css`
      /* Hides default icon on details element */
      &::-webkit-details-marker {
        display: none;
      }
      /* Hide default icon from IE. */
      &::before {
        display: none;
      }
    `,
  ],

  // Wrapper so layout works in Safari.
  summaryInner: css`
    ${Typography.base};
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
    min-height: 50px;
    padding: 10px 15px;
    white-space: nowrap;
    width: 100%;
    transition: ${Animation.transitionValue};
  `,

  caret: (isOpen: boolean) => [
    css`
      transition: ${Animation.transitionValue};
    `,
    isOpen &&
      css`
        transform: rotate(-180deg);
      `,
  ],

  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
    padding: 10px;
    border-bottom: 1px solid ${Colors.grey200};
  `,

  link: css`
    ${Typography.base};
    display: block;
    border-radius: 10px;
    color: ${Colors.grey700};
    line-height: 20px;
    font-size: 14px;
    padding: 10px 15px;

    &:hover,
    &:focus {
      color: ${Colors.black};
      background-color: ${Colors.grey100};
    }
  `,

  linkInner: css`
    transform: translateY(1px);
  `,

  linkTitle: css`
    display: block;
    font-weight: 600;
  `,

  linkDescription: css`
    display: block;
    color: ${Colors.grey700};
    margin-top: 5px;
    font-size: 12px;
    line-height: 15px;
  `,
});

const StyledLink = styled(Link)`
  display: block;
  border-radius: 10px;
  color: ${Colors.grey700};
  line-height: 20px;
  font-size: 14px;
  padding: 10px;

  &:hover,
  &:focus {
    color: ${Colors.blue500};
    background-color: ${Colors.blue100};
  }
`;
